import {Component, DestroyRef, Input, OnDestroy, OnInit, inject} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzVpbDataService,
  BokkzVpbLogic,
  CorporateTaxEntity,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-enkelvoudige-cijfers',
  templateUrl: './enkelvoudige-cijfers.component.html',
  styleUrls: ['./enkelvoudige-cijfers.component.scss'],
  imports: [
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    AsyncPipe
  ]
})
export class EnkelvoudigeCijfersComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public vpbService = inject(BokkzVpbLogic);

  public balansEnResultatenRekeningFg = new FormGroup({
    balansEnResultatenrekening: new FormArray<FormGroup>([]),
  });

  public index = 0;
  public resultaatControls: FormControl<number | null>[] = [];

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get balansEnResultatenrekening(): FormArray {
    return this.balansEnResultatenRekeningFg.get('balansEnResultatenrekening') as FormArray;
  }

  trackByFn(index: number): number {
    return index;
  }

  private readonly resultaatFieldSigns: Record<string, number> = {
    nettoOmzet: 1,
    wijzigingOnderhandenWerk: 1,
    geactiveerdeProductieEigenBedrijf: 1,
    overigeOpbrengsten: 1,
    inkoopwaardeVerkopen: -1,
    kostenUitbesteedWerk: -1,
    lonenEnSalarissen: -1,
    socialeLasten: -1,
    pensioenLasten: -1,
    overigePersoneelskosten: -1,
    ontvangenVergoedingenEnSubsidies: 1,
    afschrijvingenGoodwill: -1,
    afschrijvingOverigeImmaterieleActiva: -1,
    afschrijvingenGebouwen: -1,
    afschrijvingenMachines: -1,
    afschrijvingenOverig: -1,
    autoEnTransportKosten: -1,
    huisvestingsKosten: -1,
    onderhoudsKosten: -1,
    verkoopKosten: -1,
    overigeKosten: -1,
    financieleBaten: 1,
    financieleLasten: -1,
    resultaatDeelnemingen: 1,
    buitengewoneBaten: 1,
    buitengewoneLasten: -1,
  };

  getResultaat(entityControl: AbstractControl): number {
    const group = entityControl as FormGroup;
    return Object.entries(this.resultaatFieldSigns).reduce((total, [fieldName, sign]) => {
      const fieldArray = group.get(fieldName) as FormArray | null;
      const bedrag = fieldArray?.at(0)?.get('bedrag')?.value ?? 0;
      return total + bedrag * sign;
    }, 0);
  }

  ngOnInit(): void {
    this.balansEnResultatenRekeningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.recalculateResultaten();
      this.saveForm();
    });
  }

  private recalculateResultaten(): void {
    const formArray = this.balansEnResultatenrekening;
    while (this.resultaatControls.length < formArray.length) {
      this.resultaatControls.push(new FormControl<number | null>({value: null, disabled: true}));
    }
    this.resultaatControls.length = formArray.length;

    for (let i = 0; i < formArray.length; i++) {
      this.resultaatControls[i].setValue(this.getResultaat(formArray.at(i)), {emitEvent: false});
    }
  }

  private readonly resultatenFields: string[] = [
    'nettoOmzet', 'wijzigingOnderhandenWerk', 'geactiveerdeProductieEigenBedrijf', 'overigeOpbrengsten',
    'inkoopwaardeVerkopen', 'kostenUitbesteedWerk', 'lonenEnSalarissen', 'socialeLasten',
    'pensioenLasten', 'overigePersoneelskosten', 'ontvangenVergoedingenEnSubsidies',
    'afschrijvingenGoodwill', 'afschrijvingOverigeImmaterieleActiva', 'afschrijvingenGebouwen',
    'afschrijvingenMachines', 'afschrijvingenOverig', 'autoEnTransportKosten', 'huisvestingsKosten',
    'onderhoudsKosten', 'verkoopKosten', 'overigeKosten', 'financieleBaten', 'financieleLasten',
    'resultaatDeelnemingen', 'buitengewoneBaten', 'buitengewoneLasten'
  ];

  private isFiscaleEenheid(): boolean {
    return this.dataService.isFiscaleEenheid.value;
  }

  private getConsolidatedRow(rows: BalansEnResultatenrekening[]): BalansEnResultatenrekening | undefined {
    return rows.find(row => row.naamVennootschap === 'Consolidatie');
  }

  private ensureSingleEliminatieRow(rows: BalansEnResultatenrekening[]): BalansEnResultatenrekening[] {
    const result: BalansEnResultatenrekening[] = [];
    let firstEliminatie: BalansEnResultatenrekening | undefined;

    for (const row of rows) {
      if (row.naamVennootschap === 'Eliminatie') {
        if (!firstEliminatie) {
          firstEliminatie = row;
        }
        continue;
      }
      result.push(row);
    }

    result.push(firstEliminatie ?? ({naamVennootschap: 'Eliminatie'} as BalansEnResultatenrekening));
    return result;
  }

  private getInitialRows(vpbAangifte: VpbAangifte): BalansEnResultatenrekening[] {
    if (this.isFiscaleEenheid()) {
      const enkelvoudigeRows = this.ensureSingleEliminatieRow(
        [...(vpbAangifte.enkelvoudigeResultatenFiscaleEenheid || [])]
          .filter(row => row.naamVennootschap !== 'Consolidatie')
      );
      const consolidatedRow = {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap: 'Consolidatie',
      } as BalansEnResultatenrekening;

      return [...enkelvoudigeRows, consolidatedRow];
    }

    return [
      {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap:
          vpbAangifte.balansEnResultatenrekening?.naamVennootschap ??
          vpbAangifte.identificatie?.naamLichaam,
      } as BalansEnResultatenrekening,
    ];
  }

  private syncRowDisabledState(entityGroup: FormGroup): void {
    const isConsolidatie = entityGroup.get('naamVennootschap')?.value === 'Consolidatie';

    for (const fieldName of this.resultatenFields) {
      const fieldArray = entityGroup.get(fieldName) as FormArray | null;
      if (!fieldArray) continue;

      for (let i = 0; i < fieldArray.length; i++) {
        const bedragControl = fieldArray.at(i).get('bedrag');
        if (!bedragControl) continue;

        if (isConsolidatie) {
          bedragControl.disable({emitEvent: false});
        } else {
          bedragControl.enable({emitEvent: false});
        }
      }
    }
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = this.getInitialRows(vpbAangifte);
    const formArray = this.balansEnResultatenRekeningFg.get('balansEnResultatenrekening') as FormArray;

    this.index = lijst.length;
    if (this.index < 2) {
      this.index = 2;
    }

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i], {emitEvent: false});
        this.syncRowDisabledState(control as FormGroup);
      } else {
        const formGroup = this.dataService.createBalansEnResultatenRekeningFormGroup(lijst[i]);
        formArray.push(formGroup as FormGroup, {emitEvent: false});
        this.syncRowDisabledState(formGroup as FormGroup);
      }
    }

    this.recalculateResultaten();
  }

  private calculateConsolidatedBedragByField(rows: BalansEnResultatenrekening[], fieldName: string): number {
    return rows.reduce((sum, row) => {
      if (row.naamVennootschap === 'Consolidatie') return sum;

      const multiplier = row.naamVennootschap === 'Eliminatie' ? -1 : 1;
      const value =
        ((row as unknown as Record<string, Array<{bedrag?: number | null}> | undefined>)[fieldName]
          ?.at(0)?.bedrag ?? 0);
      return sum + value * multiplier;
    }, 0);
  }

  private buildConsolidatedFromRows(
    rows: BalansEnResultatenrekening[],
    base: BalansEnResultatenrekening
  ): BalansEnResultatenrekening {
    const consolidated = {
      ...base,
      naamVennootschap: 'Consolidatie',
    } as BalansEnResultatenrekening;

    const consolidatedRecord = consolidated as unknown as Record<
      string,
      Array<{bedrag?: number | null}> | undefined
    >;

    for (const fieldName of this.resultatenFields) {
      const bedrag = this.calculateConsolidatedBedragByField(rows, fieldName);
      const existingField = consolidatedRecord[fieldName]?.at(0) ?? {};
      consolidatedRecord[fieldName] = [{
        ...existingField,
        bedrag: bedrag === 0 ? undefined : bedrag,
      }];
    }

    return consolidated;
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  saveForm(): void {
    this.balansEnResultatenRekeningFg.updateValueAndValidity({emitEvent: false});
    const rows =
      (this.balansEnResultatenRekeningFg.getRawValue().balansEnResultatenrekening ?? []) as BalansEnResultatenrekening[];
    const isFe = this.isFiscaleEenheid();
    const enkelvoudigeRows = rows.filter(row => row.naamVennootschap !== 'Consolidatie');

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;

      const consolidatedRow = this.getConsolidatedRow(rows);
      const fallbackLastRow = rows.length > 0 ? rows[rows.length - 1] : undefined;
      const balansEnResultatenrekening = isFe
        ? this.buildConsolidatedFromRows(
          rows,
          consolidatedRow ?? fallbackLastRow ?? currentEntity.aangifte.balansEnResultatenrekening
        )
        : (rows[0] ?? currentEntity.aangifte.balansEnResultatenrekening);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening,
          enkelvoudigeResultatenFiscaleEenheid: isFe ? enkelvoudigeRows : undefined,
        } as VpbAangifte,
      } as CorporateTaxEntity;
    });
  }
}
